import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { X, KeyRound, ChevronDown, Sliders, History, Wallet, ShieldCheck, Radio, Repeat, BookOpen, Rocket, Coins, Eye, ShoppingCart, Bot, Wallet2, Grid3X3, Target, Search, ArrowLeftRight } from "lucide-react";
import {
  LayoutDashboard,
  Activity,
  Inbox,
  TrendingUp,
  LineChart,
  BarChart3,
  PieChart,
  Newspaper,
  Settings as SettingsIcon,
  ScrollText,
  Users as UsersIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAppSelector } from "@/store";
import { useGetSettingsQuery, useGetMySettingsQuery } from "@/store/api";
import { ChangePasswordDialog } from "@/components/domain/ChangePasswordDialog";

interface Item {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  end?: boolean;
  adminOnly?: boolean;
  requiresManualMode?: boolean;   // hide unless settings.approval_mode='manual'
  userGate?: string;              // for NON-admins: hide unless this /api/me/settings flag is truthy
  badge?: string;                 // small inline status badge, e.g. "paused"
  external?: boolean;             // plain <a target=_blank> (backend-served page)
}

// A section is one engine/feature: a top link plus optional indented
// sub-links (e.g. Grid Trader > Backtest / Settings).
interface Section {
  top: Item;
  children?: Item[];
}

// A collapsible nav group. Open/closed state persists per group id in
// localStorage (NAV_GROUPS_KEY). `dormant` groups get a subtle faded
// treatment for engines that are parked but not removed.
interface Group {
  id: string;
  label: string;
  defaultOpen: boolean;
  dormant?: boolean;
  sections: Section[];
}

// Overview stays ungrouped at the very top.
const overviewItem: Item = { to: "/", label: "Overview", icon: LayoutDashboard, end: true };

const groups: Group[] = [
  {
    id: "live",
    label: "Live Engines",
    defaultOpen: true,
    sections: [
      // Grid Trader — range-bound grid bot (BTC/BNB/SOL). Admin-only (live trading).
      {
        top: { to: "/grid", label: "Grid Trader", icon: Grid3X3, end: true, adminOnly: true },
        children: [
          { to: "/grid/backtest", label: "Backtest", icon: Activity, adminOnly: true },
          { to: "/grid/settings", label: "Settings", icon: Sliders, adminOnly: true },
        ],
      },
      // Inventory Swing — extract chop revenue from held inventory.
      // Admin-only (real trades on real positions).
      {
        top: { to: "/inventory-swing", label: "Inventory Swing", icon: ArrowLeftRight, end: true, adminOnly: true },
      },
      // Alpha BSC — on-chain trading via PancakeSwap + Uniswap V3 (BSC).
      // All admin-only because it controls real on-chain funds.
      {
        top: { to: "/alpha-bsc", label: "Alpha BSC", icon: Coins, end: true, adminOnly: true },
        children: [
          { to: "/alpha-bsc/watchlist", label: "Watchlist", icon: Eye, adminOnly: true },
          { to: "/alpha-bsc/manual", label: "Manual trade", icon: ShoppingCart, adminOnly: true },
          { to: "/alpha-bsc/auto", label: "Auto-strategy", icon: Bot, adminOnly: true },
          { to: "/alpha-bsc/history", label: "History", icon: History, adminOnly: true },
          { to: "/alpha-bsc/wallet", label: "BSC wallet", icon: Wallet2, adminOnly: true },
        ],
      },
      {
        top: { to: "/admin/dca", label: "DCA (Dip & Smart)", icon: Repeat, adminOnly: true },
      },
      // Hourly Bot — the original Gemini decision cycle. Hidden for members
      // who don't run the per-user cycle (e.g. hard-stopped accounts).
      {
        top: { to: "/cycles", label: "Hourly Bot", icon: Activity, userGate: "per_user_cycle_enabled" },
        children: [
          { to: "/pending", label: "Pending", icon: Inbox, requiresManualMode: true },
        ],
      },
      // Trades — every user's own fills (standalone so it survives even when
      // the Hourly Bot section is hidden).
      { top: { to: "/trades", label: "Trades", icon: TrendingUp } },
      // Harvest — the per-user accumulate + take-profit engine.
      { top: { to: "/harvest", label: "Harvest", icon: Coins } },
    ],
  },
  {
    id: "analysis",
    label: "Analysis",
    defaultOpen: true,
    sections: [
      { top: { to: "/performance", label: "Performance", icon: BarChart3 } },
      { top: { to: "/holdings", label: "Holdings", icon: Wallet, adminOnly: true } },
      { top: { to: "/portfolio", label: "Portfolio", icon: PieChart, adminOnly: true } },
      { top: { to: "/markets", label: "Markets", icon: LineChart, adminOnly: true } },
      { top: { to: "/research", label: "Deep Research", icon: Search, end: true, adminOnly: true } },
      { top: { to: "/research-report", label: "Research Report", icon: Search, adminOnly: true, external: true } },
      { top: { to: "/home-goal", label: "Home Goal 🏠", icon: PieChart, adminOnly: true, external: true } },
      { top: { to: "/admin/news", label: "News & Risk", icon: Radio, adminOnly: true } },
      { top: { to: "/intelligence", label: "Intelligence", icon: Newspaper, adminOnly: true } },
      { top: { to: "/legacy", label: "Legacy Account", icon: History, adminOnly: true } },
    ],
  },
  {
    id: "dormant",
    label: "Dormant",
    defaultOpen: false,
    dormant: true,
    sections: [
      { top: { to: "/admin/pre-pump", label: "Pre-pump", icon: Rocket, adminOnly: true } },
      { top: { to: "/admin/strategy", label: "Strategy Library", icon: BookOpen, adminOnly: true } },
    ],
  },
  {
    id: "system",
    label: "System",
    defaultOpen: false,
    sections: [
      { top: { to: "/settings", label: "Settings", icon: SettingsIcon } },
      { top: { to: "/allocation", label: "10× Allocation", icon: Target, end: true, adminOnly: true } },
      { top: { to: "/logs", label: "Logs", icon: ScrollText, adminOnly: true } },
      { top: { to: "/admin/users", label: "Users", icon: UsersIcon, adminOnly: true } },
      { top: { to: "/admin/wallet", label: "Wallet", icon: Wallet, adminOnly: true } },
      { top: { to: "/admin/audit", label: "Audit bot", icon: ShieldCheck, adminOnly: true } },
    ],
  },
];

// Open/closed state per group id, persisted across sessions.
const NAV_GROUPS_KEY = "gbb_nav_groups";

function loadGroupState(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(NAV_GROUPS_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function saveGroupState(state: Record<string, boolean>) {
  try {
    localStorage.setItem(NAV_GROUPS_KEY, JSON.stringify(state));
  } catch {
    // localStorage unavailable (private mode) — state just won't persist.
  }
}

// Mirror NavLink's matching: exact for `end` items, prefix otherwise.
function isPathActive(it: Item, pathname: string): boolean {
  if (it.end) return pathname === it.to;
  return pathname === it.to || pathname.startsWith(it.to + "/");
}

interface Props {
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
}

export function Sidebar({ mobileOpen, onCloseMobile }: Props = {}) {
  const desktop = (
    <aside className="hidden md:flex md:w-56 lg:w-64 shrink-0 border-r border-border bg-bg2 flex-col">
      <SidebarBody />
    </aside>
  );

  const mobile = mobileOpen ? (
    <div
      className="fixed inset-0 z-40 md:hidden bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onCloseMobile}
    >
      <aside
        className="absolute inset-y-0 left-0 w-64 max-w-[85vw] bg-bg2 border-r border-border flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 h-14 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-accent grid place-items-center text-white text-sm font-bold">T</div>
            <div className="font-semibold tracking-tight">Trading Bot</div>
          </div>
          <button onClick={onCloseMobile} className="text-muted hover:text-fg" aria-label="Close menu">
            <X className="w-5 h-5" />
          </button>
        </div>
        <SidebarBody onNavigate={onCloseMobile} />
      </aside>
    </div>
  ) : null;

  return (
    <>
      {desktop}
      {mobile}
    </>
  );
}

function NavItemLink({
  it,
  sub,
  showAdminBadge,
  onNavigate,
}: {
  it: Item;
  sub?: boolean;
  showAdminBadge?: boolean;
  onNavigate?: () => void;
}) {
  const Icon = it.icon;
  if (it.external) {
    // Plain <a> — opens backend-served pages (e.g. the research report)
    // in a new tab without going through React Router.
    return (
      <a
        href={it.to}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          "flex items-center gap-2.5 px-2.5 h-9 rounded-md text-muted hover:text-fg hover:bg-bg",
          sub && "h-8 pl-8 text-[13px]",
        )}
      >
        <Icon className={cn("w-4 h-4 shrink-0", sub && "w-3.5 h-3.5")} />
        <span>{it.label}</span>
        {showAdminBadge && it.adminOnly && (
          <span className="text-[10px] uppercase text-muted/80 ml-auto">admin</span>
        )}
      </a>
    );
  }
  return (
    <NavLink
      to={it.to}
      end={it.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-2.5 px-2.5 h-9 rounded-md text-muted hover:text-fg hover:bg-bg",
          sub && "h-8 pl-8 text-[13px]",
          isActive && "bg-bg text-fg",
        )
      }
    >
      <Icon className={cn("w-4 h-4 shrink-0", sub && "w-3.5 h-3.5")} />
      <span>{it.label}</span>
      {it.badge && (
        <span className="ml-auto text-[10px] uppercase px-1.5 rounded bg-warn/15 text-warn border border-warn/30">
          {it.badge}
        </span>
      )}
      {showAdminBadge && it.adminOnly && (
        <span className={cn("text-[10px] uppercase text-muted/80", it.badge ? "ml-1" : "ml-auto")}>admin</span>
      )}
    </NavLink>
  );
}

function SidebarBody({ onNavigate }: { onNavigate?: () => void } = {}) {
  const user = useAppSelector((s) => s.auth.user);
  const isAdmin = user?.role === "admin";
  const { pathname } = useLocation();
  // Pull settings so we can hide nav items that no longer apply
  // (e.g. /pending when approval_mode='auto'). Don't poll — settings
  // rarely change and the sidebar re-renders on navigation anyway.
  const settings = useGetSettingsQuery(undefined, { skip: !user });
  const approvalMode = (settings.data as any)?.approval_mode || "auto";
  // Non-admins gate some items on their OWN settings (per-user flags).
  const mySettings = useGetMySettingsQuery(undefined, { skip: !user || isAdmin });
  const my = (mySettings.data as any) || {};

  const itemVisible = (it: Item) => {
    if (it.adminOnly && !isAdmin) return false;
    if (it.requiresManualMode && approvalMode !== "manual") return false;
    if (it.userGate && !isAdmin && !my[it.userGate]) return false;
    return true;
  };

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const stored = loadGroupState();
    const state: Record<string, boolean> = {};
    for (const g of groups) state[g.id] = stored[g.id] ?? g.defaultOpen;
    return state;
  });

  // Auto-open a collapsed group when the current route lives inside it,
  // so the active link is never hidden (e.g. arriving via Ctrl+K palette).
  useEffect(() => {
    setOpenGroups((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const g of groups) {
        const containsActive = g.sections.some(
          (s) => isPathActive(s.top, pathname) || (s.children ?? []).some((c) => isPathActive(c, pathname)),
        );
        if (containsActive && !next[g.id]) {
          next[g.id] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [pathname]);

  const toggleGroup = (id: string) => {
    setOpenGroups((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      saveGroupState(next);
      return next;
    });
  };

  const [pwOpen, setPwOpen] = useState(false);
  return (
    <>
      <div className="hidden md:flex px-4 h-14 items-center gap-2 border-b border-border">
        <div className="w-7 h-7 rounded-md bg-accent grid place-items-center text-white text-sm font-bold">T</div>
        <div className="font-semibold tracking-tight">Trading Bot</div>
      </div>
      <nav className="flex-1 px-2 py-3 space-y-0.5 text-sm overflow-y-auto">
        <NavItemLink it={overviewItem} onNavigate={onNavigate} />

        {groups.map((g) => {
          // Apply role/mode gating first; a group whose items are all
          // hidden must not render at all.
          const sections = g.sections
            .filter((s) => itemVisible(s.top))
            .map((s) => ({ top: s.top, children: (s.children ?? []).filter(itemVisible) }));
          if (sections.length === 0) return null;

          const open = !!openGroups[g.id];
          // Small groups (1-2 visible sections) always show their
          // sub-items; bigger ones only expand the active section.
          const small = sections.length <= 2;

          return (
            <div key={g.id} className={cn(g.dormant && "opacity-70")}>
              <button
                type="button"
                onClick={() => toggleGroup(g.id)}
                aria-expanded={open}
                className="w-full flex items-center justify-between mt-4 mb-1 px-2.5 text-[10px] uppercase tracking-wider text-muted/70 hover:text-fg"
              >
                <span>{g.label}</span>
                <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", !open && "-rotate-90")} />
              </button>
              {open &&
                sections.map((s) => {
                  // Suppress the "admin" tag inside sections that are
                  // entirely admin-only (Grid / Alpha BSC / Inventory
                  // Swing) — it would just repeat on every row.
                  const allAdmin = !!s.top.adminOnly && s.children.every((c) => c.adminOnly);
                  const sectionActive =
                    isPathActive(s.top, pathname) || s.children.some((c) => isPathActive(c, pathname));
                  return (
                    <div key={s.top.to}>
                      <NavItemLink it={s.top} showAdminBadge={!allAdmin} onNavigate={onNavigate} />
                      {s.children.length > 0 &&
                        (small || sectionActive) &&
                        s.children.map((c) => (
                          <NavItemLink key={c.to} it={c} sub showAdminBadge={!allAdmin} onNavigate={onNavigate} />
                        ))}
                    </div>
                  );
                })}
            </div>
          );
        })}
      </nav>
      <div className="px-3 py-3 border-t border-border text-xs text-muted">
        {user?.email ? (
          <div className="space-y-1.5">
            <div className="space-y-0.5">
              <div className="truncate text-fg/90">{user.email}</div>
              <div className="text-muted/70">{isAdmin ? "Administrator" : "Member · 10% profit-share"}</div>
            </div>
            <button
              onClick={() => setPwOpen(true)}
              className="flex items-center gap-1.5 text-muted hover:text-fg transition-colors"
              title="Change your password"
            >
              <KeyRound className="w-3.5 h-3.5" />
              Change password
            </button>
          </div>
        ) : "v2"}
      </div>
      <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
    </>
  );
}
